import { initContract } from '@ts-rest/core';
import { z } from 'zod';

import {
  CLINICAL_TASK_CATEGORIES,
  CLINICAL_TASK_PRIORITIES,
  CLINICAL_TASK_STATUSES,
  PRACTITIONER_REFERENCE_REGEX,
} from '@eluve/clinical-tasks';

const c = initContract();

const errorResponseSchema = z.object({
  error: z.string(),
});

/**
 * The clinician-editable fields of a Clinical Task. Category/priority reuse
 * the shared vocabulary from `@eluve/clinical-tasks` (same enums the
 * extraction writes), so the edge rejects unknown codes before the service
 * re-validates.
 */
const taskFieldsSchema = z.object({
  description: z.string().trim().min(1),
  category: z.enum(CLINICAL_TASK_CATEGORIES),
  priority: z.enum(CLINICAL_TASK_PRIORITIES),
  /** ISO date (YYYY-MM-DD); null = no planned date. zod's .date() also
   *  rejects impossible calendar dates (2026-02-30) — those would otherwise
   *  die as an opaque FHIR transaction 500. */
  dueDate: z.string().date().nullable(),
  /** Assignee, `Practitioner/<id>`. Null semantics per route: create →
   *  default to the creator; update → leave unchanged (owner is never
   *  cleared). */
  ownerReference: z.string().regex(PRACTITIONER_REFERENCE_REGEX).nullable(),
});

const createTaskBodySchema = taskFieldsSchema
  .extend({
    /** The session the task is pre-scoped to — the server derives the FHIR
     *  Patient/Encounter references from it; clients never send references.
     *  Omitted for a standalone task created from the tenant Tasks view. */
    appointmentId: z.string().optional(),
    /** Standalone (no-encounter) create from the Tasks view: the tenant
     *  patient the task is for. The server resolves the FHIR Patient from it
     *  and the task carries no Encounter. Exactly one of appointmentId /
     *  patientId is present. */
    patientId: z.string().uuid().optional(),
  })
  .refine((b) => Boolean(b.appointmentId) !== Boolean(b.patientId), {
    message: 'Provide exactly one of appointmentId or patientId',
  });

const updateTaskBodySchema = taskFieldsSchema.extend({
  /** Session scope guard: the task must belong to this appointment. */
  appointmentId: z.string(),
  /** Lifecycle status — folds into FHIR intent + the review-status tag
   *  server-side (draft = pending review; every other value = attested,
   *  written with reviewer attestation Provenance). Edit-only: create always
   *  starts a task in the accepted (To do) state. OPTIONAL for rollout skew:
   *  an old web bundle omits it, and the server then leaves status untouched
   *  (a plain in-place field edit). */
  status: z.enum(CLINICAL_TASK_STATUSES).optional(),
});

/**
 * Task-domain surface of the clinical-tasks mini-app (ENG-5243): RESTful
 * create/update of Clinical Tasks. Deliberately app-specific (no `:agentId`)
 * — unlike the generic mini-apps review routes, "a task" only exists for this
 * app. The generic `findings`/`review`/`findings/edit` routes are untouched
 * and keep serving the pending-review flow (and already-deployed bundles).
 */
export const clinicalTasksContract = c.router(
  {
    createTask: {
      method: 'POST',
      path: '/tasks',
      pathParams: z.object({
        tenantId: z.string(),
      }),
      body: createTaskBodySchema,
      responses: {
        201: z.object({ id: z.string() }),
        400: errorResponseSchema,
        403: errorResponseSchema,
        404: errorResponseSchema,
        422: errorResponseSchema,
      },
      summary:
        "Manually create a Clinical Task for a session. Born accepted (requested/order + attested) — human-authored tasks skip the draft review pile. Written through the creating clinician's own Medplum identity, with a creation Provenance.",
    },
    updateTask: {
      method: 'PUT',
      path: '/tasks/:taskId',
      pathParams: z.object({
        tenantId: z.string(),
        taskId: z.string(),
      }),
      body: updateTaskBodySchema,
      responses: {
        200: z.object({ updated: z.literal(true) }),
        400: errorResponseSchema,
        403: errorResponseSchema,
        404: errorResponseSchema,
        422: errorResponseSchema,
      },
      summary:
        "Edit a still-pending Clinical Task's fields (description, category, priority, planned date, assignee). RESTful successor of the generic findings/edit route for this app; the task stays pending.",
    },
    regenerateTasks: {
      method: 'POST',
      path: '/tasks/regenerate',
      pathParams: z.object({
        tenantId: z.string(),
      }),
      body: z.object({
        /** The session whose suggested tasks are regenerated. */
        appointmentId: z.string(),
      }),
      responses: {
        200: z.object({
          regenerated: z.literal(true),
          /** Pending drafts tombstoned to `cancelled` before the re-run. */
          cancelledDrafts: z.number(),
        }),
        400: errorResponseSchema,
        403: errorResponseSchema,
        404: errorResponseSchema,
        422: errorResponseSchema,
      },
      summary:
        "Regenerate a session's suggested Clinical Tasks (alpha) — a fresh slate for everything not accepted: tombstones the still-pending drafts to `cancelled` and un-attests previously rejected tombstones (both kept un-attested so re-extraction may re-propose them), then re-enqueues the extraction — which picks up the clinician's saved custom instructions. Accepted tasks are untouched and stay suppressed.",
    },
    reviewTask: {
      method: 'POST',
      path: '/tasks/:taskId/review',
      pathParams: z.object({
        tenantId: z.string(),
        taskId: z.string(),
      }),
      body: z.object({
        /** Session scope guard: the task must belong to this appointment. */
        appointmentId: z.string(),
        decision: z.enum(['approve', 'reject']),
      }),
      responses: {
        200: z.object({ reviewed: z.literal(true) }),
        400: errorResponseSchema,
        403: errorResponseSchema,
        404: errorResponseSchema,
        422: errorResponseSchema,
      },
      summary:
        'Accept or reject ONE pending Clinical Task (approve → requested/order, reject → cancelled tombstone; both + attestation Provenance). RESTful successor of the generic review route for this app; 404 when the task is not pending for the session.',
    },
  },
  { pathPrefix: '/:tenantId/mini-apps/clinical-tasks' },
);
